// TipTap node for images embedded in a broadcast email body. Serializes
// to/from exactly `{ type: 'image', attrs: { key, alt, width } }`, the shape
// the server's `BroadcastDocSchema`
// (apps/server/src/_platform/email/broadcast-doc-schema.ts) validates. `key`
// is a processed-media key (`attachments/{userId}/{uuid}`) returned by the
// finalize step; the email serializer projects it to an absolute src at send
// time. Custom node (not @tiptap/extension-image) to match the codebase's
// hand-rolled node convention (see MergeTag.ts) and pin getJSON() to the
// stored shape — no `src` attribute leaks into the document.
//
// Atom semantics: a block-level, non-editable unit — the caret can't enter
// it, Backspace removes it whole. `parseHTML` matches only `img[data-key]`,
// so a pasted external <img> (no data-key) never becomes a node — the only
// images in a broadcast are ones the author uploaded through the composer.

import { mergeAttributes, Node } from '@tiptap/core';
import { mediaUrlFromKey } from '@leanin/shared/media-url';

export const BroadcastImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-key'),
        renderHTML: (attributes) =>
          attributes['key'] ? { 'data-key': attributes['key'] as string } : {},
      },
      alt: {
        default: '',
        parseHTML: (element: HTMLElement) => element.getAttribute('alt') ?? '',
        renderHTML: (attributes) => ({ alt: (attributes['alt'] as string) ?? '' }),
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const w = element.getAttribute('width');
          if (w === null) return null;
          const n = Number.parseInt(w, 10);
          return Number.isFinite(n) ? n : null;
        },
        renderHTML: (attributes) =>
          attributes['width'] ? { width: attributes['width'] as number } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[data-key]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const key = node.attrs['key'] as string | null;
    // Display-only src for the composer (relative — resolves against the web
    // origin). It is NOT a node attribute, so it never appears in getJSON();
    // the stored doc carries only `key`, and the email serializer builds the
    // absolute src from it at render time.
    const src = key !== null ? (mediaUrlFromKey(key) ?? '') : '';
    return ['img', mergeAttributes(HTMLAttributes, { src })];
  },

  renderText({ node }) {
    const alt = node.attrs['alt'] as string;
    return alt.length > 0 ? `[Image: ${alt}]` : '[Image]';
  },
});
